import { registerHandler } from '@/modules/events/eventBus.service';
import type {
  InvoiceEventPayload,
  LogisticsScheduledPayload,
  ReservationEventPayload,
} from '@/modules/events/types';
import { sendTemplatedEmail } from '@/modules/communications/email/email.service';
import { buildWebhookPayload } from '@/modules/communications/webhooks/webhook-payloads';
import { dispatchOutboundWebhook } from '@/modules/communications/webhooks/webhook-dispatcher';

async function runCommunication(
  event: Parameters<typeof buildWebhookPayload>[0],
  payload: {
    actorUid: string;
    actorEmail?: string;
    companyId: string;
    occurredAt: string;
  },
  data: Record<string, unknown>,
  emailTo?: string,
  emailVars?: Record<string, string | number>,
): Promise<void> {
  if (emailTo && emailVars) {
    await sendTemplatedEmail(event, emailTo, emailVars);
  }
  const wh = buildWebhookPayload(event, payload, data);
  await dispatchOutboundWebhook(wh);
}

async function onReservationCreated(p: ReservationEventPayload): Promise<void> {
  await runCommunication(
    'reservation.created',
    p,
    {
      reservationId: p.reservationId,
      title: p.title,
      status: p.status,
      startDate: p.startDate,
      endDate: p.endDate,
    },
    p.actorEmail,
    {
      title: p.title,
      startDate: p.startDate,
      endDate: p.endDate,
      status: p.status,
    },
  );
}

async function onReservationCancelled(p: ReservationEventPayload): Promise<void> {
  await runCommunication(
    'reservation.cancelled',
    p,
    {
      reservationId: p.reservationId,
      title: p.title,
      status: p.status,
      startDate: p.startDate,
      endDate: p.endDate,
    },
    p.actorEmail,
    { title: p.title, startDate: p.startDate, endDate: p.endDate, status: p.status },
  );
}

async function onInvoiceCreated(p: InvoiceEventPayload): Promise<void> {
  await runCommunication(
    'invoice.created',
    p,
    {
      invoiceId: p.invoiceId,
      status: p.status,
      total: p.total,
      currency: p.currency,
    },
    p.actorEmail,
    {
      invoiceId: p.invoiceId,
      status: p.status,
      total: p.total,
      currency: p.currency,
    },
  );
}

async function onLogisticsScheduled(p: LogisticsScheduledPayload): Promise<void> {
  await runCommunication(
    'logistics.scheduled',
    p,
    {
      orderId: p.orderId,
      scheduledDate: p.scheduledDate,
      type: p.type,
    },
    p.actorEmail,
    {
      orderId: p.orderId,
      scheduledDate: p.scheduledDate,
      type: p.type,
    },
  );
}

let registered = false;

export function registerCommunicationEventHandlers(): void {
  if (registered) return;
  registered = true;

  registerHandler('reservation.created', onReservationCreated);
  registerHandler('reservation.cancelled', onReservationCancelled);
  registerHandler('invoice.created', onInvoiceCreated);
  registerHandler('logistics.scheduled', onLogisticsScheduled);
}
